import React from 'react';
import { shopVar } from '@/lib/shopStyles';
import { useCart } from '@/providers/CartProvider';
import { useShop } from '@/providers/ShopProvider';
import * as m from "@/paraglide/messages.js"
import clsx from 'clsx';

// FAS-2016 — how often the code may be used is a campaign setting now. The API sends
// `maxUses` (null = unlimited); an older API that does not send it only ever had
// single-use shop-unlock codes, so "1 order only" stays right for it.
const usesDescription = (maxUses: number | null | undefined): string => {
    if (maxUses === null) {
        return m.unlock_code_unlimited();
    }
    if (maxUses === undefined || maxUses === 1) {
        return m.unlock_code_description_in_card();
    }
    return m.unlock_code_max_orders({ maxOrders: maxUses });
};

export const ShopUnlockCard: React.FC = () => {
    const { cart, removeCoupon } = useCart();
    const { shop, isWidget } = useShop();

    if (!cart?.appliedCouponCode?.shopUnlock) {
        return null;
    }

    const shopUnlock = cart.appliedCouponCode.shopUnlock;
    const isCurrentShop = shopUnlock.slug.slug === shop.slug;

    if (!isCurrentShop) {
        return null;
    }

    const maxProducts = shopUnlock.maxProducts;
    const productsInMaxProducts = shopUnlock.products_in_max_products;

    // Calculate current product count based on the limit
    let currentProductCount = 0;
    if (productsInMaxProducts === null) {
        // Count all products in cart
        currentProductCount = cart.products.reduce((total, product) => total + product.quantity, 0);
    } else {
        // Count only products that are in the products_in_max_products array
        currentProductCount = cart.products
            .filter(product => productsInMaxProducts.includes(product.productId))
            .reduce((total, product) => total + product.quantity, 0);
    }

    return (
        <div className={clsx('rounded-md justify-between items-center', isWidget ? 'mt-4' : 'py-2 px-8')}>
            <div 
                className="border rounded-md px-4 pt-4 pb-4 shadow-md"
                style={{ 
                    borderColor: shopVar('--shop-borderColor'),
                    backgroundColor: shopVar('--shop-panelBackgroundColor'),
                }}
            >
                 <div className="flex items-start border-b border-dashed w-full pb-4" style={{ borderColor: shopVar('--shop-borderColor') }}>
                     <div className="flex-grow pr-2">
                         <div className="flex items-center justify-between">
                             <div className="flex items-center">
                                 {/* Lock Icon */}
                                 <div 
                                     className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mr-3"
                                     style={{ backgroundColor: shopVar('--shop-ctaButtonColor') }}
                                 >
                                     <svg 
                                         className="w-4 h-4" 
                                         fill="currentColor" 
                                         viewBox="0 0 20 20"
                                         style={{ color: shopVar('--shop-ctaButtonTextColor') }}
                                     >
                                         <path 
                                             fillRule="evenodd" 
                                             d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" 
                                             clipRule="evenodd" 
                                         />
                                     </svg>
                                 </div>
                                 <h3 className="text-base font-bold" style={{ color: shopVar('--shop-textColor') }}>
                                     {m.access_code_applied()}
                                 </h3>
                             </div>
                             <div className="flex items-center gap-2">
                                 <span 
                                     className="text-xs px-2 py-1 rounded font-mono"
                                     style={{ 
                                         backgroundColor: shopVar('--shop-borderColor'),
                                         color: shopVar('--shop-textColor')
                                     }}
                                 >
                                     {cart.appliedCouponCode.code}
                                 </span>
                                 <button
                                     onClick={removeCoupon}
                                     className="text-xs px-2 py-1 rounded hover:opacity-80 transition-opacity"
                                     style={{
                                         backgroundColor: shopVar('--shop-removeFromCartButtonColor'),
                                         color: shopVar('--shop-removeFromCartButtonTextColor'),
                                     }}
                                 >
                                     {m.remove_coupon()}
                                 </button>
                             </div>
                         </div>
                         <p className="font-normal text-xs tracking-tight mt-1" style={{ color: shopVar('--shop-textColor') }}>
                             {usesDescription(cart.appliedCouponCode.maxUses)}
                         </p>
                     </div>
                 </div>
                
                {maxProducts !== null && (
                    <div className="pt-2">
                        <div className="">
                            <span className="text-sm font-medium" style={{ color: shopVar('--shop-textColor') }}>
                                {m.unlock_code_product_limit({
                                    currentProductCount: currentProductCount,
                                    maxProducts: maxProducts
                                })}
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};